/**
 * PortalPreferenciasPage — preferências/consentimentos do parceiro (story A4).
 *
 * Hoje: gerenciar o consentimento de NOTA DE VOZ (LGPD) — ver status + conceder
 * / revogar. Revogar bloqueia NOVAS gravações (o gate 412 da A2 protege o
 * backend); áudio já enviado segue a retenção (A5/F4). O modal de aceite do
 * composer usa o mesmo grant; aqui é o ponto de gestão/revogação.
 *
 * D3 (passo 19): a seção Avisos (som, push e níveis por categoria) vem antes da nota de voz.
 */

import { AlertCircle, CheckCircle2, Loader2, Mic } from "lucide-react";
import { toast } from "sonner";

import { PreferenciasDeAviso } from "@/components/notifications/PreferenciasDeAviso";
import { VARIANTE_DO_PORTAL } from "@/components/portal/variantePreferenciasDoPortal";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  useGrantVoiceConsent,
  useRevokeVoiceConsent,
  useVoiceConsentStatus,
} from "@/hooks/usePortalAtendimento";

function formatDate(iso?: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("pt-BR");
}

export function PortalPreferenciasPage() {
  const statusQuery = useVoiceConsentStatus();
  const grantMutation = useGrantVoiceConsent();
  const revokeMutation = useRevokeVoiceConsent();

  const granted = statusQuery.data?.granted ?? false;
  const busy = grantMutation.isPending || revokeMutation.isPending;

  const handleGrant = () => {
    grantMutation.mutate(undefined, {
      onSuccess: () => toast.success("Consentimento de nota de voz concedido."),
      onError: () => toast.error("Não foi possível conceder o consentimento."),
    });
  };

  const handleRevoke = () => {
    revokeMutation.mutate(undefined, {
      onSuccess: () =>
        toast.success("Consentimento revogado. Novas notas de voz ficam bloqueadas."),
      onError: () => toast.error("Não foi possível revogar o consentimento."),
    });
  };

  return (
    <div className="container mx-auto max-w-3xl p-4 sm:p-6 lg:p-8 space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-emerald-900">Preferências</h1>
        <p className="text-sm text-slate-600 mt-1">
          Como o portal avisa você e o que você permite.
        </p>
      </div>

      {/* D3 (passo 19, peça J): os avisos antes do consentimento — é o que o parceiro mexe no dia a dia; o consentimento é uma vez.
          A seção traz o "Som neste aparelho", os "Avisos com o portal fechado" e "Como você quer ser avisado". */}
      <PreferenciasDeAviso variante={VARIANTE_DO_PORTAL} />

      <Card className="border-emerald-100/80 bg-white/80 backdrop-blur-sm">
        <CardHeader className="border-b border-emerald-50 pb-4">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center">
              <Mic className="h-5 w-5" />
            </div>
            <CardTitle className="text-emerald-900">Nota de voz</CardTitle>
          </div>
          <CardDescription>
            Controle o envio de mensagens de áudio no atendimento (LGPD).
          </CardDescription>
        </CardHeader>

        <CardContent className="pt-6 space-y-4">
          {statusQuery.isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-emerald-600" />
            </div>
          ) : statusQuery.isError ? (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>Erro ao carregar o status do consentimento.</AlertDescription>
            </Alert>
          ) : (
            <>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Status</span>
                  <Badge variant={granted ? "default" : "outline"}>
                    {granted ? "Permitido" : "Bloqueado"}
                  </Badge>
                </div>
                <p className="text-xs text-slate-500">
                  Concedido em: {formatDate(statusQuery.data?.granted_at)}
                </p>
                {statusQuery.data?.revoked_at && (
                  <p className="text-xs text-slate-500">
                    Revogado em: {formatDate(statusQuery.data?.revoked_at)}
                  </p>
                )}
              </div>

              {granted ? (
                <Alert className="border-emerald-200 bg-emerald-50">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                  <AlertDescription className="text-emerald-900 text-sm">
                    Você pode enviar notas de voz no atendimento.
                  </AlertDescription>
                </Alert>
              ) : (
                <Alert className="border-amber-200 bg-amber-50">
                  <AlertCircle className="h-4 w-4 text-amber-600" />
                  <AlertDescription className="text-amber-900 text-sm">
                    Notas de voz estão bloqueadas. Conceda o consentimento para enviá-las (também é
                    pedido ao gravar pela primeira vez).
                  </AlertDescription>
                </Alert>
              )}

              <div className="flex gap-2">
                {granted ? (
                  <Button type="button" variant="outline" onClick={handleRevoke} disabled={busy}>
                    {revokeMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Revogar consentimento
                  </Button>
                ) : (
                  <Button type="button" onClick={handleGrant} disabled={busy}>
                    {grantMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Conceder consentimento
                  </Button>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default PortalPreferenciasPage;
